import { ButtonGhostV2, colors2, List } from "oolib";
import React, { useState } from "react";
import styled from "styled-components";
import {
  Lightbox,
  LightboxTrigger,
  LightboxContent,
} from "@/components/UI/lightbox";
import { checkIfSortPathsAreMatching } from "../../../../utils/tableSortUtils";

// Hidden means GONE, not transparent: an invisible button still took its
// width, so the header text ellipsised early for no visible reason.
const StyledButtonWrapper = styled.div`
  display: ${({ isVisible }) => (isVisible ? "inline-flex" : "none")};

  button {
    padding: 0.4rem;
  }

  /* Apply active styles when button is active (dropdown open only) */
  ${({ isOpen }) =>
    isOpen &&
    `
    button {
      background-color: ${colors2.grey5};

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.04);
        pointer-events: none;
        z-index: 1;
      }
    }
  `}
`;

export const ColumnSortDropdown = ({
  colData,
  isBackendSort,
  backendActiveSort,
  activeSorts,
  onSortClick,
  isHovered,
}) => {
  const [open, setOpen] = useState(false);

  // Anchoring (centred on the button), repositioning on scroll/column-resize,
  // and click-outside dismissal are all handled by the shared Lightbox
  // (floating-ui `autoUpdate` + `shift` + `useDismiss`), replacing the
  // hand-rolled rAF loop this component used to run.

  // Determine current active sort
  let activeSortForDisplay;
  if (isBackendSort) {
    activeSortForDisplay = !checkIfSortPathsAreMatching(
      backendActiveSort?.sort,
      colData?.backendSortConfig?.sort
    )
      ? undefined
      : Object.values(backendActiveSort.sort)[0] === 1
      ? "ascending"
      : "descending";
  } else {
    activeSortForDisplay =
      activeSorts?.[colData?.colId] === "none" || !activeSorts?.[colData?.colId]
        ? undefined
        : activeSorts[colData.colId] === "a"
        ? "ascending"
        : "descending";
  }

  // Determine button icon and visibility
  const hasActiveSort = !!activeSortForDisplay;
  const buttonIcon = hasActiveSort
    ? activeSortForDisplay === "ascending"
      ? "ArrowDown"
      : "ArrowUp"
    : "FunnelSimple";

  // Button is visible if: open, has active sort, or cell is hovered
  const isButtonVisible = open || hasActiveSort || isHovered;

  // Sort options. A ranked column (sortConfig.order, see useSort_FRONTEND)
  // has no alphabet: descending is its order[0] first, so the words say so.
  const ranked = !isBackendSort && !!colData?.sortConfig?.order;
  const sortOptions = [
    {
      value: "ascending",
      display: ranked ? "Lowest first" : "Sort A-Z",
      icon: "ArrowDown",
    },
    {
      value: "descending",
      display: ranked ? "Highest first" : "Sort Z-A",
      icon: "ArrowUp",
    },
  ];

  const handleSortOptionClick = (option) => {
    if (onSortClick) {
      onSortClick(option.value);
    }
    setOpen(false);
  };

  return (
    // Centred on the button (previously a manual `left: buttonCenterX` +
    // `x: "-50%"` transform). crossAxisOffset={0} because the shared default of
    // 2 would sit the panel 2px off-centre.
    <Lightbox
      open={open}
      onOpenChange={setOpen}
      placement="bottom"
      crossAxisOffset={0}
    >
      {/* The wrapper, not the button, is the anchor — same element the old
          buttonRef measured, so the panel lines up exactly as before. asChild
          merges floating-ui's own click handler onto it alongside this
          stopPropagation, which the button used to carry. */}
      <LightboxTrigger asChild>
        <StyledButtonWrapper
          isVisible={isButtonVisible}
          isOpen={open}
          onClick={(e) => e.stopPropagation()}
        >
          <ButtonGhostV2 iconSize="S" S icon={buttonIcon} />
        </StyledButtonWrapper>
      </LightboxTrigger>
      <LightboxContent style={{ zIndex: 10000000000000000 }}>
        <List
          options={sortOptions}
          value={sortOptions.find((op) => op.value === activeSortForDisplay)}
          onChange={(id, option) => handleSortOptionClick(option)}
          disableShadow={true}
          enableBorder={false}
          style={{ padding: 2 }}
        />
      </LightboxContent>
    </Lightbox>
  );
};
